import React, { useMemo, useRef, useState } from 'react';
import {
  CaretLeft, CaretRight, Calendar as CalendarIcon,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  getWallClock, toDayKeyInTz, hourFractionInTz, timeLabelInTz,
  detectBrowserTimezone, wallClockToUTC,
} from '../lib/tz';

// =============================================================================
// Shared utilities
// =============================================================================
const pad = (n) => String(n).padStart(2, '0');

// "Day cursor" helpers — these operate on plain (year, month, day) tuples so
// they're timezone-agnostic. We track the "selected day" the user is viewing
// as a Date whose UTC midnight roughly corresponds to that day. The grid then
// projects items into the user's chosen tz at render time.
export const toDayKey = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const sameDay = (a, b) => toDayKey(a) === toDayKey(b);
export const addDays = (d, n) => {
  const c = new Date(d); c.setDate(c.getDate() + n); return c;
};
export const startOfWeek = (d) => {
  // Monday-first week (uses local calendar — fine for navigation cursor).
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  const day = (c.getDay() + 6) % 7;
  return addDays(c, -day);
};
export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

function fmtRange(start, end, viewKind) {
  if (viewKind === 'day') {
    return start.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (viewKind === 'week') {
    const sMonth = start.toLocaleString(undefined, { month: 'short' });
    const eMonth = end.toLocaleString(undefined, { month: 'short' });
    const year = end.getFullYear();
    if (start.getMonth() === end.getMonth()) {
      return `${sMonth} ${start.getDate()} – ${end.getDate()}, ${year}`;
    }
    return `${sMonth} ${start.getDate()} – ${eMonth} ${end.getDate()}, ${year}`;
  }
  if (viewKind === 'year') {
    return String(start.getFullYear());
  }
  return start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function ToolbarNav({ cursor, setCursor, viewKind }) {
  const navigate = (dir) => {
    if (viewKind === 'day') {
      setCursor(addDays(cursor, dir));
    } else if (viewKind === 'week') {
      setCursor(addDays(cursor, 7 * dir));
    } else if (viewKind === 'year') {
      const c = new Date(cursor);
      c.setFullYear(c.getFullYear() + dir, 0, 1);
      setCursor(c);
    } else {
      const c = new Date(cursor);
      c.setMonth(c.getMonth() + dir, 1);
      setCursor(c);
    }
  };
  const goToday = () => setCursor(new Date());

  let start, end;
  if (viewKind === 'day') {
    start = new Date(cursor); start.setHours(0, 0, 0, 0);
    end = start;
  } else if (viewKind === 'week') {
    start = startOfWeek(cursor);
    end = addDays(start, 6);
  } else if (viewKind === 'year') {
    start = new Date(cursor.getFullYear(), 0, 1);
    end = new Date(cursor.getFullYear(), 11, 31);
  } else {
    start = startOfMonth(cursor);
    end = endOfMonth(cursor);
  }

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0"
              onClick={() => navigate(-1)} aria-label="Previous">
        <CaretLeft size={14} weight="bold" />
      </Button>
      <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={goToday}>
        Today
      </Button>
      <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0"
              onClick={() => navigate(1)} aria-label="Next">
        <CaretRight size={14} weight="bold" />
      </Button>
      <span className="ml-2 font-heading text-base font-semibold">
        {fmtRange(start, end, viewKind)}
      </span>
    </div>
  );
}

function eventTone(item) {
  return {
    bg: `${item.color || '#002677'}1A`,    // 10% tint
    accent: item.color || '#002677',
    fg: '#0a0a0a',
  };
}

function timeLabel(date, tz) {
  return tz ? timeLabelInTz(date, tz)
            : date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// ---- Drag-to-move / drag-to-resize helpers (Day & Week views) ----------------
const SNAP_MIN = 15;        // 15-minute snap, matches Google Calendar default
const RESIZE_ZONE_PX = 8;   // bottom-edge sensitivity for resize cursor
const DRAG_TRIGGER_PX = 3;  // pointer travel before a click becomes a drag
const snapMin = (m) => Math.round(m / SNAP_MIN) * SNAP_MIN;

// Convert (date, minutesFromMidnight, tz) → UTC ISO by treating the minutes as
// wall-clock in the chosen timezone.
function dayMinutesToIso(date, minutes, zone) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const utc = wallClockToUTC(
    date.getFullYear(), date.getMonth() + 1, date.getDate(),
    h, m, zone,
  );
  return utc.toISOString();
}

// Pretty "HH:mm" from minutes-from-midnight.
function fmtClock(min) {
  const h = Math.floor(min / 60) % 24;
  const m = Math.abs(min) % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Floating tooltip that follows the cursor during a drag-move / drag-resize.
function DragTooltip({ x, y, label, sub }) {
  if (x == null || y == null) return null;
  return (
    <div
      role="tooltip"
      data-testid="schedule-drag-tooltip"
      className="fixed pointer-events-none z-[100] rounded-sm bg-[#002677] text-white px-2.5 py-1.5 text-xs font-semibold shadow-lg"
      style={{
        left: x + 16,
        top:  y + 16,
        whiteSpace: 'nowrap',
        boxShadow: '0 6px 18px rgba(0,38,119,0.35)',
      }}
    >
      <div className="leading-tight">{label}</div>
      {sub && (
        <div className="leading-tight text-[10px] uppercase tracking-wider opacity-80 font-medium">
          {sub}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Day view (single-column hour grid)
// =============================================================================
export function ScheduleDayView({ cursor, setCursor, items, onOpenItem, onCreateAt, onUpdate, tz }) {
  const zone = tz || detectBrowserTimezone();
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const day = useMemo(() => { const d = new Date(cursor); d.setHours(0, 0, 0, 0); return d; }, [cursor]);
  const dayKey = toDayKey(day);
  const [drag, setDrag] = useState(null);
  // drag = { id, kind: 'move'|'resize', startY, startTop, startHeight, top, height, moved }

  const dayItems = useMemo(() => {
    const out = [];
    items.forEach((ev) => {
      if (ev.status === 'cancelled') return;
      const start = new Date(ev.start_at);
      if (isNaN(start.getTime())) return;
      // Project the event into the target tz to decide which calendar day it
      // falls on (e.g. a 23:30 UTC event may be "tomorrow" in Tokyo).
      if (toDayKeyInTz(start, zone) !== dayKey) return;
      const hf = hourFractionInTz(start, zone);
      const minutesFromTop = (hf - HOUR_START) * 60;
      if (minutesFromTop < 0) return;
      const duration = Math.max(15, Number(ev.duration_minutes) || 30);
      out.push({
        ev,
        top:    (minutesFromTop / 60) * HOUR_PX,
        height: Math.max(20, (duration / 60) * HOUR_PX),
      });
    });
    return out;
  }, [items, dayKey, zone]);

  const isToday = sameDay(day, today);

  return (
    <div data-testid="schedule-day-view">
      <div className="flex items-center justify-between mb-4">
        <ToolbarNav cursor={cursor} setCursor={setCursor} viewKind="day" />
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-background">
        <div className="grid" style={{ gridTemplateColumns: '80px 1fr' }}>
          <div className="border-b border-r border-border" />
          <div className={`border-b border-border p-3 ${isToday ? 'bg-[#002677]/5' : ''}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              {day.toLocaleDateString(undefined, { weekday: 'long' })}
            </div>
            <div className={`text-2xl font-heading font-bold ${isToday ? 'text-[#002677]' : 'text-foreground'}`}>
              {day.getDate()}
            </div>
          </div>
        </div>
        <div className="grid relative" style={{ gridTemplateColumns: '80px 1fr' }}>
          <div className="relative border-r border-border" style={{ height: HOURS.length * HOUR_PX }}>
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute text-[11px] text-muted-foreground text-right pr-2 font-mono w-full"
                style={{ top: h * HOUR_PX - 6, height: 12, lineHeight: '12px' }}
              >
                {pad(h)}:00
              </div>
            ))}
          </div>
          <div className="relative" style={{ height: HOURS.length * HOUR_PX }}>
            {HOURS.map((h, i) => (
              <button
                key={h}
                type="button"
                onClick={() => onCreateAt?.({
                  year:   day.getFullYear(),
                  month:  day.getMonth() + 1,
                  day:    day.getDate(),
                  hour:   h,
                  minute: 0,
                  tz:     zone,
                })}
                data-testid={`schedule-day-cell-${h}`}
                aria-label={`Create appointment at ${pad(h)}:00`}
                className="block w-full border-b border-border/60 text-left transition-colors hover:bg-[#002677]/5"
                style={{ height: HOUR_PX, background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.012)' }}
              />
            ))}
            {dayItems.map(({ ev, top, height }) => {
              const tone = eventTone(ev);
              const isDragging = drag && drag.id === ev.id;
              const liveTop    = isDragging ? drag.top    : top;
              const liveHeight = isDragging ? drag.height : height;
              const canDrag = Boolean(onUpdate) && ev.status !== 'cancelled';

              const onPointerDown = (e) => {
                if (!canDrag) return;
                if (e.button !== undefined && e.button !== 0) return;
                e.stopPropagation();
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const offsetY = e.clientY - rect.top;
                const kind = offsetY >= rect.height - RESIZE_ZONE_PX ? 'resize' : 'move';
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
                setDrag({
                  id: ev.id, kind, startY: e.clientY,
                  startTop: top, startHeight: height,
                  top, height, moved: false,
                  cx: e.clientX, cy: e.clientY,
                });
              };
              const onPointerMove = (e) => {
                if (!isDragging) return;
                const dy = e.clientY - drag.startY;
                if (!drag.moved && Math.abs(dy) < DRAG_TRIGGER_PX) return;
                const snappedMin = snapMin((dy / HOUR_PX) * 60);
                const snappedPx  = (snappedMin / 60) * HOUR_PX;
                const maxY       = HOURS.length * HOUR_PX;
                let next = { ...drag, moved: true, cx: e.clientX, cy: e.clientY };
                if (drag.kind === 'move') {
                  next.top = Math.max(0, Math.min(maxY - drag.startHeight, drag.startTop + snappedPx));
                } else {
                  next.height = Math.max(
                    (SNAP_MIN / 60) * HOUR_PX,
                    Math.min(maxY - drag.startTop, drag.startHeight + snappedPx),
                  );
                }
                setDrag(next);
              };
              const onPointerUp = () => {
                if (!isDragging) return;
                const committed = drag.moved && (
                  drag.top !== drag.startTop || drag.height !== drag.startHeight
                );
                if (committed) {
                  if (drag.kind === 'move') {
                    const newMin = Math.round((drag.top / HOUR_PX) * 60);
                    const newIso = dayMinutesToIso(day, newMin, zone);
                    onUpdate?.(ev.id, { start_at: newIso });
                  } else {
                    const newDur = Math.max(SNAP_MIN, Math.round((drag.height / HOUR_PX) * 60));
                    onUpdate?.(ev.id, { duration_minutes: newDur });
                  }
                } else {
                  // No movement → treat as a click on the event card
                  onOpenItem?.(ev);
                }
                setDrag(null);
              };

              return (
                <div
                  key={ev.id}
                  role="button"
                  tabIndex={0}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={() => setDrag(null)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpenItem?.(ev); }}
                  data-testid={`schedule-day-event-${ev.id}`}
                  className="absolute left-2 right-3 rounded-sm px-3 py-1.5 text-left overflow-hidden select-none hover:shadow-md transition-shadow"
                  style={{
                    top: liveTop, height: liveHeight,
                    background: tone.bg,
                    borderLeft: `4px solid ${tone.accent}`,
                    color: tone.fg,
                    cursor: isDragging
                      ? (drag.kind === 'resize' ? 'ns-resize' : 'grabbing')
                      : (canDrag ? 'grab' : 'pointer'),
                    touchAction: 'none',
                    boxShadow: isDragging ? '0 8px 24px rgba(0,38,119,0.18)' : undefined,
                    zIndex: isDragging ? 20 : 'auto',
                  }}
                >
                  <div className="text-sm font-semibold truncate pointer-events-none">
                    {ev.customer_name || ev.title || 'Appointment'}
                  </div>
                  <div className="text-xs text-muted-foreground truncate pointer-events-none">
                    {timeLabel(new Date(ev.start_at), zone)} · {ev.duration_minutes || 30} min · {ev.title || ''}
                  </div>
                  {canDrag && (
                    <div
                      aria-hidden
                      className="absolute bottom-0 left-0 right-0 pointer-events-none"
                      style={{
                        height: RESIZE_ZONE_PX,
                        borderBottom: `2px solid ${tone.accent}`,
                        opacity: 0.35,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {drag && drag.moved && (() => {
        const startMin = drag.kind === 'resize'
          ? Math.round((drag.startTop / HOUR_PX) * 60)
          : Math.round((drag.top / HOUR_PX) * 60);
        const endMin   = drag.kind === 'resize'
          ? Math.round(((drag.startTop + drag.height) / HOUR_PX) * 60)
          : Math.round(((drag.top + drag.startHeight) / HOUR_PX) * 60);
        const dur = endMin - startMin;
        return (
          <DragTooltip
            x={drag.cx}
            y={drag.cy}
            label={`${fmtClock(startMin)} – ${fmtClock(endMin)}`}
            sub={`${day.toLocaleDateString(undefined, { weekday: 'short' })} · ${dur} min`}
          />
        );
      })()}
    </div>
  );
}

// =============================================================================
// Week view (7-column day lanes with hour grid)
// =============================================================================
const HOUR_START = 0;   // 00:00
const HOUR_END   = 24;  // 23:00 (last row at 23:00)
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
const HOUR_PX = 44; // each hour row height

export function ScheduleWeekView({ cursor, setCursor, items, onOpenItem, onCreateAt, onUpdate, tz }) {
  const zone = tz || detectBrowserTimezone();
  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const dayKeys = useMemo(() => days.map((d) => toDayKey(d)), [days]);
  const lanesRef = useRef(null);
  const [drag, setDrag] = useState(null);
  // drag = { id, kind, startY, startX, startTop, startHeight, startLane, top, height, lane, moved }

  // Bucket items by day, computing pixel offset + height in the chosen tz
  const byDay = useMemo(() => {
    const m = days.map(() => []);
    items.forEach((ev) => {
      if (ev.status === 'cancelled') return;
      const start = new Date(ev.start_at);
      if (isNaN(start.getTime())) return;
      const key = toDayKeyInTz(start, zone);
      const idx = dayKeys.indexOf(key);
      if (idx < 0) return;
      const hf = hourFractionInTz(start, zone);
      const minutesFromTop = (hf - HOUR_START) * 60;
      if (minutesFromTop < 0) return;
      const duration = Math.max(15, Number(ev.duration_minutes) || 30);
      m[idx].push({
        ev,
        top:    (minutesFromTop / 60) * HOUR_PX,
        height: Math.max(20, (duration / 60) * HOUR_PX),
      });
    });
    return m;
  }, [days, dayKeys, items, zone]);

  return (
    <div data-testid="schedule-week-view">
      <div className="flex items-center justify-between mb-4">
        <ToolbarNav cursor={cursor} setCursor={setCursor} viewKind="week" />
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-background">
        {/* Header row */}
        <div className="grid" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
          <div className="border-b border-r border-border" />
          {days.map((d) => {
            const isToday = sameDay(d, today);
            return (
              <div
                key={toDayKey(d)}
                className={`border-b border-r border-border last:border-r-0 p-2 text-center ${isToday ? 'bg-[#002677]/5' : ''}`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                  {d.toLocaleDateString(undefined, { weekday: 'short' })}
                </div>
                <div className={`text-lg font-heading font-bold ${isToday ? 'text-[#002677]' : 'text-foreground'}`}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        {/* Body — hour grid */}
        <div
          ref={lanesRef}
          className="grid relative"
          style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}
        >
          {/* Hour gutter */}
          <div className="relative border-r border-border" style={{ height: HOURS.length * HOUR_PX }}>
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute text-[10px] text-muted-foreground text-right pr-1.5 font-mono w-full"
                style={{ top: h * HOUR_PX - 6, height: 12, lineHeight: '12px' }}
              >
                {pad(h)}:00
              </div>
            ))}
          </div>
          {/* Day lanes */}
          {days.map((d, idx) => (
            <div
              key={toDayKey(d)}
              className="relative border-r border-border last:border-r-0"
              style={{ height: HOURS.length * HOUR_PX }}
            >
              {HOURS.map((h, i) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => onCreateAt?.({
                    year:   d.getFullYear(),
                    month:  d.getMonth() + 1,
                    day:    d.getDate(),
                    hour:   h,
                    minute: 0,
                    tz:     zone,
                  })}
                  data-testid={`schedule-week-cell-${idx}-${h}`}
                  aria-label={`Create appointment ${d.toLocaleDateString()} ${pad(h)}:00`}
                  className="block w-full border-b border-border/60 text-left transition-colors hover:bg-[#002677]/5"
                  style={{ height: HOUR_PX, background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.012)' }}
                />
              ))}
              {byDay[idx].map(({ ev, top, height }) => {
                const tone = eventTone(ev);
                const isDragging = drag && drag.id === ev.id;
                const liveTop    = isDragging ? drag.top    : top;
                const liveHeight = isDragging ? drag.height : height;
                const liveLane   = isDragging ? drag.lane   : idx;
                // Hide the original lane render of a moved event; render only at active lane.
                if (isDragging && liveLane !== idx) return null;
                const canDrag = Boolean(onUpdate) && ev.status !== 'cancelled';

                const onPointerDown = (e) => {
                  if (!canDrag) return;
                  if (e.button !== undefined && e.button !== 0) return;
                  e.stopPropagation();
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const offsetY = e.clientY - rect.top;
                  const kind = offsetY >= rect.height - RESIZE_ZONE_PX ? 'resize' : 'move';
                  try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
                  setDrag({
                    id: ev.id, kind,
                    startY: e.clientY, startX: e.clientX,
                    startTop: top, startHeight: height, startLane: idx,
                    top, height, lane: idx, moved: false,
                    cx: e.clientX, cy: e.clientY,
                  });
                };
                const onPointerMove = (e) => {
                  if (!isDragging) return;
                  const dy = e.clientY - drag.startY;
                  const dx = e.clientX - drag.startX;
                  if (!drag.moved && Math.abs(dy) < DRAG_TRIGGER_PX && Math.abs(dx) < DRAG_TRIGGER_PX) return;
                  const snappedMin = snapMin((dy / HOUR_PX) * 60);
                  const snappedPx  = (snappedMin / 60) * HOUR_PX;
                  const maxY       = HOURS.length * HOUR_PX;
                  let next = { ...drag, moved: true, cx: e.clientX, cy: e.clientY };
                  if (drag.kind === 'move') {
                    next.top = Math.max(0, Math.min(maxY - drag.startHeight, drag.startTop + snappedPx));
                    // Horizontal lane swap based on cursor X relative to the lanes row
                    const row = lanesRef.current;
                    if (row) {
                      const rowRect = row.getBoundingClientRect();
                      const laneAreaLeft = rowRect.left + 60; // 60px gutter
                      const laneWidth = (rowRect.width - 60) / 7;
                      const rel = e.clientX - laneAreaLeft;
                      const newLane = Math.max(0, Math.min(6, Math.floor(rel / laneWidth)));
                      next.lane = newLane;
                    }
                  } else {
                    next.height = Math.max(
                      (SNAP_MIN / 60) * HOUR_PX,
                      Math.min(maxY - drag.startTop, drag.startHeight + snappedPx),
                    );
                  }
                  setDrag(next);
                };
                const onPointerUp = () => {
                  if (!isDragging) return;
                  const committed = drag.moved && (
                    drag.top !== drag.startTop ||
                    drag.height !== drag.startHeight ||
                    drag.lane !== drag.startLane
                  );
                  if (committed) {
                    if (drag.kind === 'move') {
                      const newMin = Math.round((drag.top / HOUR_PX) * 60);
                      const targetDay = days[drag.lane] || d;
                      const newIso = dayMinutesToIso(targetDay, newMin, zone);
                      onUpdate?.(ev.id, { start_at: newIso });
                    } else {
                      const newDur = Math.max(SNAP_MIN, Math.round((drag.height / HOUR_PX) * 60));
                      onUpdate?.(ev.id, { duration_minutes: newDur });
                    }
                  } else {
                    onOpenItem?.(ev);
                  }
                  setDrag(null);
                };

                return (
                  <div
                    key={ev.id}
                    role="button"
                    tabIndex={0}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={() => setDrag(null)}
                    onKeyDown={(e) => { if (e.key === 'Enter') onOpenItem?.(ev); }}
                    data-testid={`schedule-week-event-${ev.id}`}
                    className="absolute left-1 right-1 rounded-sm px-2 py-1 text-left overflow-hidden select-none hover:shadow-md transition-shadow"
                    style={{
                      top: liveTop, height: liveHeight,
                      background: tone.bg,
                      borderLeft: `3px solid ${tone.accent}`,
                      color: tone.fg,
                      cursor: isDragging
                        ? (drag.kind === 'resize' ? 'ns-resize' : 'grabbing')
                        : (canDrag ? 'grab' : 'pointer'),
                      touchAction: 'none',
                      boxShadow: isDragging ? '0 8px 24px rgba(0,38,119,0.18)' : undefined,
                      zIndex: isDragging ? 20 : 'auto',
                    }}
                  >
                    <div className="text-[11px] font-semibold truncate pointer-events-none">
                      {ev.customer_name || ev.title || 'Appointment'}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate pointer-events-none">
                      {timeLabel(new Date(ev.start_at), zone)} · {ev.duration_minutes || 30} min
                    </div>
                    {canDrag && (
                      <div
                        aria-hidden
                        className="absolute bottom-0 left-0 right-0 pointer-events-none"
                        style={{
                          height: RESIZE_ZONE_PX,
                          borderBottom: `2px solid ${tone.accent}`,
                          opacity: 0.35,
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {drag && drag.moved && (() => {
        const startMin = drag.kind === 'resize'
          ? Math.round((drag.startTop / HOUR_PX) * 60)
          : Math.round((drag.top / HOUR_PX) * 60);
        const endMin   = drag.kind === 'resize'
          ? Math.round(((drag.startTop + drag.height) / HOUR_PX) * 60)
          : Math.round(((drag.top + drag.startHeight) / HOUR_PX) * 60);
        const dur = endMin - startMin;
        const targetDay = days[drag.lane] || days[drag.startLane];
        return (
          <DragTooltip
            x={drag.cx}
            y={drag.cy}
            label={`${fmtClock(startMin)} – ${fmtClock(endMin)}`}
            sub={`${targetDay.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · ${dur} min`}
          />
        );
      })()}
    </div>
  );
}

// =============================================================================
// Month view (classic calendar grid)
// =============================================================================
export function ScheduleMonthView({ cursor, setCursor, items, onOpenItem, onCreateAt, tz }) {
  const zone = tz || detectBrowserTimezone();
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);

  const cells = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const firstDow = (monthStart.getDay() + 6) % 7; // Mon=0
    const totalDays = endOfMonth(cursor).getDate();
    const out = [];
    for (let i = 0; i < firstDow; i++) {
      out.push({ key: `pre-${i}`, date: null });
    }
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(cursor.getFullYear(), cursor.getMonth(), d);
      out.push({ key: `d-${d}`, date });
    }
    // pad trailing so the grid is filled (multiples of 7)
    while (out.length % 7) out.push({ key: `post-${out.length}`, date: null });
    return out;
  }, [cursor]);

  // Bucket items by yyyy-mm-dd as observed in the chosen timezone
  const byDay = useMemo(() => {
    const m = new Map();
    items.forEach((ev) => {
      const d = new Date(ev.start_at);
      if (isNaN(d.getTime())) return;
      const key = toDayKeyInTz(d, zone);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(ev);
    });
    // sort each day by time
    for (const arr of m.values()) {
      arr.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    }
    return m;
  }, [items, zone]);

  return (
    <div data-testid="schedule-month-view">
      <div className="flex items-center justify-between mb-4">
        <ToolbarNav cursor={cursor} setCursor={setCursor} viewKind="month" />
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-background">
        <div className="grid grid-cols-7 border-b border-border bg-muted/30">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((w) => (
            <div key={w} className="p-2 text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((c) => {
            if (!c.date) {
              return <div key={c.key} className="min-h-[110px] border-b border-r border-border last:border-r-0 bg-muted/10" />;
            }
            const isToday = sameDay(c.date, today);
            const dayItems = byDay.get(toDayKey(c.date)) || [];
            const visible = dayItems.slice(0, 3);
            const overflow = dayItems.length - visible.length;
            return (
              <div
                key={c.key}
                onClick={(e) => {
                  // Only fire if the click landed on the cell background (not on a child button)
                  if (e.target === e.currentTarget || e.target.dataset?.role === 'cell-bg') {
                    onCreateAt?.({
                      year:   c.date.getFullYear(),
                      month:  c.date.getMonth() + 1,
                      day:    c.date.getDate(),
                      hour:   9,
                      minute: 0,
                      tz:     zone,
                    });
                  }
                }}
                data-role="cell-bg"
                className={`min-h-[110px] border-b border-r border-border last:border-r-0 p-1.5 cursor-pointer hover:bg-[#002677]/5 transition-colors ${isToday ? 'bg-[#002677]/5' : ''}`}
                data-testid={`schedule-month-cell-${toDayKey(c.date)}`}
              >
                <div data-role="cell-bg" className={`text-xs font-semibold mb-1 ${isToday ? 'text-[#002677]' : 'text-foreground'}`}>
                  {c.date.getDate()}
                </div>
                <div className="space-y-0.5">
                  {visible.map((ev) => {
                    const tone = eventTone(ev);
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => onOpenItem?.(ev)}
                        data-testid={`schedule-month-event-${ev.id}`}
                        className="w-full text-left rounded-sm px-1.5 py-0.5 text-[10px] truncate hover:shadow-sm transition-shadow"
                        style={{
                          background: tone.bg,
                          borderLeft: `3px solid ${tone.accent}`,
                          opacity: ev.status === 'cancelled' ? 0.45 : 1,
                          textDecoration: ev.status === 'cancelled' ? 'line-through' : 'none',
                        }}
                      >
                        <span className="font-mono text-[9px] text-muted-foreground mr-1">
                          {timeLabel(new Date(ev.start_at), zone)}
                        </span>
                        {ev.customer_name || ev.title || 'Appointment'}
                      </button>
                    );
                  })}
                  {overflow > 0 && (
                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                      <CalendarIcon size={10} /> +{overflow} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// Year view (12 mini-month calendars; click a day to drill into Day view)
// =============================================================================
export function ScheduleYearView({
  cursor,
  setCursor,
  items,
  onDrilldown,
  tz,
  syncPrompt,        // { isGuest, googleConnected, onConnect, onExport }
}) {
  const zone = tz || detectBrowserTimezone();
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);

  // Bucket items by yyyy-mm-dd in chosen timezone for whole year
  const byDay = useMemo(() => {
    const m = new Map();
    items.forEach((ev) => {
      const d = new Date(ev.start_at);
      if (isNaN(d.getTime())) return;
      const key = toDayKeyInTz(d, zone);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(ev);
    });
    return m;
  }, [items, zone]);

  const year = cursor.getFullYear();
  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, i) =>
      new Date(year, i, 1).toLocaleString(undefined, { month: 'long' })),
    [year],
  );

  return (
    <div data-testid="schedule-year-view">
      <div className="flex items-center justify-between mb-4">
        <ToolbarNav cursor={cursor} setCursor={setCursor} viewKind="year" />
      </div>

      {/* "Sync this calendar with Google so it stays in your pocket" — */}
      {/* shown when there are appointments AND the user hasn't connected Google. */}
      {syncPrompt && items.length > 0 && !syncPrompt.googleConnected && (
        <div
          className="mb-4 rounded-sm border border-[#002677]/20 bg-gradient-to-r from-[#002677]/5 to-transparent p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="schedule-year-google-prompt"
        >
          <div
            className="h-10 w-10 rounded-sm flex items-center justify-center flex-shrink-0"
            style={{ background: '#002677' }}
            aria-hidden
          >
            <CalendarIcon size={20} weight="fill" color="#ffffff" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">
              Sync this calendar with Google so it stays in your pocket.
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {syncPrompt.isGuest
                ? 'Create a free account to auto-sync — or download the .ics file to import right now.'
                : 'Connect Google Calendar to two-way sync — or download an .ics file for any calendar app.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={syncPrompt.onExport}
              className="h-9 px-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold rounded-sm border border-border bg-background hover:bg-accent/40 transition-colors"
              data-testid="schedule-year-export-ics"
            >
              Download .ics
            </button>
            <button
              type="button"
              onClick={syncPrompt.onConnect}
              className="h-9 px-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold rounded-sm bg-[#002677] text-white hover:bg-[#002677]/90 transition-colors"
              data-testid="schedule-year-connect-google"
            >
              {syncPrompt.isGuest ? 'Create free account' : 'Connect Google'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 12 }, (_, monthIdx) => {
          const monthStart = new Date(year, monthIdx, 1);
          const firstDow = (monthStart.getDay() + 6) % 7; // Mon=0
          const totalDays = new Date(year, monthIdx + 1, 0).getDate();
          const cells = [];
          for (let i = 0; i < firstDow; i++) cells.push(null);
          for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, monthIdx, d));
          while (cells.length % 7) cells.push(null);

          // count events in this month
          const monthCount = cells.reduce((acc, c) => {
            if (!c) return acc;
            return acc + (byDay.get(toDayKey(c))?.length || 0);
          }, 0);

          return (
            <div
              key={monthIdx}
              className="border border-border rounded-sm bg-background p-3"
              data-testid={`schedule-year-month-${monthIdx}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <button
                  type="button"
                  onClick={() => onDrilldown?.(monthStart, 'month')}
                  className="font-heading text-sm font-semibold text-foreground hover:text-[#002677] transition-colors"
                  data-testid={`schedule-year-month-label-${monthIdx}`}
                >
                  {monthNames[monthIdx]}
                </button>
                {monthCount > 0 && (
                  <span className="text-[10px] uppercase tracking-wider text-[#002677] font-semibold">
                    {monthCount} {monthCount === 1 ? 'appt' : 'appts'}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-7 gap-px text-center mb-1">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((w, i) => (
                  <div key={i} className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold py-0.5">
                    {w}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-px">
                {cells.map((c, i) => {
                  if (!c) return <div key={`pad-${i}`} className="aspect-square" />;
                  const isToday = sameDay(c, today);
                  const dayEvents = byDay.get(toDayKey(c)) || [];
                  const hasEvents = dayEvents.length > 0;
                  const tone = hasEvents ? eventTone(dayEvents[0]) : null;
                  return (
                    <button
                      key={`d-${c.getDate()}`}
                      type="button"
                      onClick={() => onDrilldown?.(c, 'day')}
                      data-testid={`schedule-year-day-${toDayKey(c)}`}
                      title={
                        hasEvents
                          ? `${dayEvents.length} appointment${dayEvents.length > 1 ? 's' : ''}`
                          : c.toLocaleDateString()
                      }
                      className={`aspect-square text-[10px] rounded-sm flex items-center justify-center relative transition-colors ${
                        isToday
                          ? 'bg-[#002677] text-white font-semibold'
                          : hasEvents
                            ? 'font-semibold text-foreground hover:bg-[#002677]/10'
                            : 'text-muted-foreground hover:bg-accent/40'
                      }`}
                      style={
                        hasEvents && !isToday
                          ? { background: tone.bg, borderBottom: `2px solid ${tone.accent}` }
                          : undefined
                      }
                    >
                      {c.getDate()}
                      {hasEvents && dayEvents.length > 1 && !isToday && (
                        <span
                          className="absolute top-0.5 right-0.5 text-[7px] font-bold"
                          style={{ color: tone.accent }}
                        >
                          {dayEvents.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
